import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiLayers, FiClipboard, FiRepeat, FiMessageSquare,
  FiPlus, FiFileText, FiUser, FiDownload, FiClock,
} from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { useArtifactsStats } from '../../context/ArtifactsStatsContext';
import '../../styles/ArtifactsSidebar.css';

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Skel: React.FC<{ w?: string | number; h?: number; r?: number; style?: React.CSSProperties }> = ({
  w = '100%', h = 10, r = 5, style,
}) => (
  <div className="art-sb-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const SkeletonStats: React.FC = () => (
  <div className="art-sb-stats">
    {[0,1,2,3].map(i => (
      <div className="art-sb-stat-card" key={i}>
        <Skel w={26} h={26} r={8} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Skel w="55%" h={14} r={4} />
          <Skel w="75%" h={8} r={3} />
        </div>
      </div>
    ))}
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(d?: string): string {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function fmtDateTime(d: string): string {
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return d; }
}

function formatFilename(filename: string): string {
  const noExt = filename.replace(/\.[^.]+$/, '');
  return noExt.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const FLOW_CFG = {
  tasks:    { label: 'CMD',  color: '#8b5cf6', icon: <FiClipboard size={11} /> },
  routines: { label: 'RT',   color: '#00b0d3', icon: <FiRepeat size={11} /> },
  chats:    { label: 'CH',   color: '#22c55e', icon: <FiMessageSquare size={11} /> },
};

// ── Component ─────────────────────────────────────────────────────────────────
const ArtifactsSidebarContent: React.FC = () => {
  const navigate = useNavigate();
  const { summary, recentItems, summaryLoading, activeArtifactId, versions } = useArtifactsStats();

  const total    = summary?.total    ?? 0;
  const tasks    = summary?.tasks    ?? 0;
  const routines = summary?.routines ?? 0;
  const chats    = summary?.chats    ?? 0;

  // Distribution bar percentages
  const distTotal   = tasks + routines + chats || 1;
  const tasksPct    = Math.round((tasks    / distTotal) * 100);
  const routinesPct = Math.round((routines / distTotal) * 100);
  const chatsPct    = 100 - tasksPct - routinesPct;

  const quickActions = [
    { label: 'New Command', icon: <FiPlus size={13} />,      color: '#a200ff', iconBg: 'rgba(162,0,255,0.14)',  route: ROUTES.MOMENTUM_COMMAND + '?new=1' },
    { label: 'Routines',    icon: <FiRepeat size={13} />,    color: '#00b0d3', iconBg: 'rgba(0,176,211,0.14)',  route: ROUTES.MOMENTUM_ROUTINES           },
    { label: 'Commands',    icon: <FiClipboard size={13} />, color: '#6366f1', iconBg: 'rgba(99,102,241,0.14)', route: ROUTES.MOMENTUM_COMMAND            },
    { label: 'Agents',      icon: <FiUser size={13} />,      color: '#f59e0b', iconBg: 'rgba(245,158,11,0.14)', route: ROUTES.DIGITAL_WORKFORCE           },
  ];

  const showVersionsOnly = !!activeArtifactId;

  return (
    <div className="art-sb-wrap">
      <div className="art-sb-body">

        {/* ── Stats (hidden when viewing versioned artifact) ── */}
        {!showVersionsOnly && (summaryLoading ? (
          <SkeletonStats />
        ) : (
          <div className="art-sb-stats">
            {[
              { icon: <FiLayers size={14} />,        iconBg: 'rgba(162,0,255,0.12)',  iconColor: '#a200ff', val: total,    lbl: 'Total Artifacts', sub: 'All sources'     },
              { icon: <FiClipboard size={14} />,     iconBg: 'rgba(99,102,241,0.12)', iconColor: '#6366f1', val: tasks,    lbl: 'Commands',        sub: 'Task outputs'    },
              { icon: <FiRepeat size={14} />,        iconBg: 'rgba(0,176,211,0.12)',  iconColor: '#00b0d3', val: routines, lbl: 'Routines',        sub: 'Routine outputs' },
              { icon: <FiMessageSquare size={14} />, iconBg: 'rgba(139,92,246,0.12)', iconColor: '#8b5cf6', val: chats,    lbl: 'Chats',           sub: 'Chat outputs'    },
            ].map((s, i) => (
              <div className="art-sb-stat-card" key={i}>
                <div className="art-sb-stat-card__icon" style={{ background: s.iconBg, color: s.iconColor }}>
                  {s.icon}
                </div>
                <div className="art-sb-stat-card__body">
                  <span className="art-sb-stat-card__val">{s.val.toLocaleString()}</span>
                  <span className="art-sb-stat-card__lbl">{s.lbl}</span>
                </div>
                <span className="art-sb-stat-card__sub">{s.sub}</span>
              </div>
            ))}
          </div>
        ))}

        {/* ── Version History (shown whenever an artifact is open, with an empty
              state when it has no prior versions) ── */}
        {activeArtifactId && (
          <div className="art-sb-section">
            <div className="art-sb-section-lbl">
              Version History
              {versions.length > 0 && <span className="art-ver__count">{versions.length}</span>}
            </div>
            {versions.length === 0 ? (
              <div className="art-ver__empty">
                <FiClock size={16} />
                <span>No version history for this artifact yet.</span>
              </div>
            ) : (
            <div className="art-ver__list">
              {versions.map((v, i) => {
                const isActive = v.id === activeArtifactId;
                const ext = (v.filename.match(/\.([^.]+)$/) || ['',''])[1].toUpperCase();
                return (
                  <button
                    key={v.id}
                    className={`art-ver__row${isActive ? ' art-ver__row--active' : ''}`}
                    onClick={() => { if (!isActive) navigate(`/artifacts/${v.id}`); }}
                  >
                    <div className="art-ver__dot-col">
                      <span className={`art-ver__dot${isActive ? ' art-ver__dot--active' : v.is_latest ? ' art-ver__dot--latest' : ''}`} />
                      {i < versions.length - 1 && <span className="art-ver__line" />}
                    </div>
                    <div className="art-ver__info">
                      <div className="art-ver__top">
                        <div className="art-ver__file-icon">
                          <FiFileText size={12} />
                          {ext && <span className="art-ver__ext">{ext}</span>}
                        </div>
                        <div className="art-ver__meta">
                          <span className="art-ver__label">
                            Version {v.version_number}
                            {v.is_latest && <span className="art-ver__badge">Latest</span>}
                            {isActive && !v.is_latest && <span className="art-ver__badge art-ver__badge--viewing">Viewing</span>}
                          </span>
                          <span className="art-ver__date">
                            {v.created_at ? fmtDateTime(v.created_at) : '—'}
                          </span>
                          {v.routine_run_id && (
                            <span className="art-ver__run">Run #{v.routine_run_id}</span>
                          )}
                        </div>
                        <a
                          href={v.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="art-ver__download"
                          onClick={e => e.stopPropagation()}
                          title="Download this version"
                        >
                          <FiDownload size={12} />
                        </a>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* ── Quick Actions (hidden when viewing versioned artifact) ── */}
        {!showVersionsOnly && <div className="art-sb-section">
          <div className="art-sb-section-lbl">Quick Actions</div>
          <div className="art-sb-qa-grid">
            {quickActions.map((a, i) => (
              <button key={i} className="art-sb-qa-btn" onClick={() => navigate(a.route)}>
                <div className="art-sb-qa-icon" style={{ background: a.iconBg, color: a.color }}>{a.icon}</div>
                <span className="art-sb-qa-label">{a.label}</span>
              </button>
            ))}
          </div>
        </div>}

        {!showVersionsOnly && <>
          {/* ── Recent Files ── */}
          {recentItems.length > 0 && (
            <div className="art-sb-section">
              <div className="art-sb-section-lbl">Recent Files</div>
              <div className="art-sb-recent-list">
                {recentItems.map((item) => {
                  const cfg = FLOW_CFG[item.flow] ?? FLOW_CFG.tasks;
                  return (
                    <button
                      key={item.id}
                      className="art-sb-recent-item"
                      onClick={() => navigate(`/artifacts/${item.id}`)}
                    >
                      <div className="art-sb-recent-item__icon" style={{ background: `${cfg.color}18`, color: cfg.color }}>
                        <FiFileText size={12} />
                      </div>
                      <div className="art-sb-recent-item__body">
                        <span className="art-sb-recent-item__name">{formatFilename(item.filename)}</span>
                        <div className="art-sb-recent-item__meta">
                          <span className="art-sb-recent-item__badge" style={{ color: cfg.color, background: `${cfg.color}18`, borderColor: `${cfg.color}35` }}>
                            {cfg.icon} {cfg.label}
                          </span>
                          <span className="art-sb-recent-item__time">{timeAgo(item.created_at)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── File Distribution ── */}
          {!summaryLoading && total > 0 && (
            <div className="art-sb-section">
              <div className="art-sb-section-lbl">Artifact Distribution</div>
              <div className="art-sb-dist-card">
                <div className="art-sb-dist-bar">
                  {tasksPct    > 0 && <div className="art-sb-dist-bar__tasks"    style={{ width: `${tasksPct}%` }} />}
                  {routinesPct > 0 && <div className="art-sb-dist-bar__routines" style={{ width: `${routinesPct}%` }} />}
                  {chatsPct    > 0 && <div className="art-sb-dist-bar__chats"    style={{ width: `${chatsPct}%` }} />}
                </div>
                <div className="art-sb-dist-legend">
                  {[
                    { dot: 'art-sb-dist-dot--tasks',    label: 'Commands', val: tasks,    pct: tasksPct    },
                    { dot: 'art-sb-dist-dot--routines', label: 'Routines', val: routines, pct: routinesPct },
                    { dot: 'art-sb-dist-dot--chats',    label: 'Chats',    val: chats,    pct: chatsPct    },
                  ].map((row, i) => (
                    <div key={i} className="art-sb-dist-row">
                      <span className={`art-sb-dist-dot ${row.dot}`} />
                      <span className="art-sb-dist-lbl">{row.label}</span>
                      <span className="art-sb-dist-val">{row.val}</span>
                      <span className="art-sb-dist-pct">{row.pct}%</span>
                    </div>
                  ))}
                </div>
                <div className="art-sb-dist-total">
                  <FiLayers size={11} />
                  <span>{total.toLocaleString()} artifact{total !== 1 ? 's' : ''} total</span>
                </div>
              </div>
            </div>
          )}
        </>}

      </div>
    </div>
  );
};

export default ArtifactsSidebarContent;
